import * as Link from './link';
import * as Map from './map';
import { ExtendedObject, MiniInfo } from './info';
import { Extends, Config } from './index';

type PathDescribe = [
	// 路由到模块的基本路径
	string[],
	// 模块名称
	string,
	// 模块所属 APP ID
	string,
	// 如果为 object 则表示通过 Restful 模式匹配，其为匹配参数
	object | undefined
];

/** 路径递归函数接口 */
interface pathRecursion<Ext extends ExtendedObject = {user: object}> {
	/**
	 * @param path     模块内路径数组
	 * @param module   模块
	 * @param appId    应用 Id
	 * @param info     处理用基本信息
	 * @param restful  restful 参数，如果不为 restful 模式，请忽略此参数
	*/
	(
		path: string[],
		module: string,
		appId: string,
		info: MiniInfo<Ext>,
		restful?: object
	): AsyncIterableIterator<PathDescribe>;
	/**
	 * 路径匹配
	 * @param path     模块内路径数组
	 * @param module   模块
	 * @param appId    应用 Id
	 * @param basePath 模块对应的路由路径数组
	 * @param info     处理用基本信息
	 * @param restful  restful 参数，如果不为 restful 模式，请忽略此参数
	*/
	(
		path: string[],
		module: string | string[],
		appId: string,
		basePath: string[],
		info: MiniInfo<Ext>,
		restful?: object
	): AsyncIterableIterator<PathDescribe>;
}
function getRestful(a: any, b: any): undefined | object {
	if (!(typeof a === 'object' && a && typeof b === 'object' && b)) {
		return typeof a === 'object' && a || typeof b === 'object' && b || undefined;
	}
	const ret = {};
	for (let k in new Set([...Reflect.ownKeys(a), ...Reflect.ownKeys(b)])) {
		const descriptor: PropertyDescriptor | undefined = Reflect.getOwnPropertyDescriptor(b, k) || Reflect.getOwnPropertyDescriptor(a, k);
		if (!descriptor) { continue; }
		Reflect.defineProperty(ret, k, descriptor);
	}
	return ret;
}
/** 路径匹配函数接口 */
function pathRecursion<Ext extends ExtendedObject = {user: object}>(
	this: undefined | { getLink?: Link.get<Ext>; config?: Config; extendsInt?: Extends; },
	p: string[],
	module: string,
	appId: string,
	info: MiniInfo<Ext>,
	restful?: object,
): AsyncIterableIterator<PathDescribe>;
function pathRecursion<Ext extends ExtendedObject = {user: object}>(
	this: undefined | { getLink?: Link.get<Ext>; config?: Config; extendsInt?: Extends; },
	p: string[],
	module: string,
	appId: string,
	basePath: string[],
	info: MiniInfo<Ext>,
	restful?: object,
): AsyncIterableIterator<PathDescribe>;
async function* pathRecursion<Ext extends ExtendedObject = {user: object}>(
	this: undefined | { getLink?: Link.get<Ext>; config?: Config; extendsInt?: Extends; },
	path: string[],
	module: string,
	appId: string,
	basePath: string[] | MiniInfo<Ext>,
	info?: MiniInfo<Ext> | object,
	restful?: object,
): AsyncIterableIterator<PathDescribe> {
	const getLink = this && this.getLink || Link.get;
	const extendsInt = this && this.config && this.config.extends || (() => {}) as Extends
	if (!Array.isArray(basePath)) { [basePath, info, restful] = [[], basePath, info]; }
	//是否未输出原始路径
	let base = true;
	//模块路径数组化
	const brn = module.split(/\\\//).filter(Boolean);
	// 联合模块的路径
	const allPath = [...brn, ...path];
	//考虑到模块路径深度过高，将祖先路径进行后续枚举
	for(let length = brn.length; length >= 0; length--) {
		//获取 link
		const pl = getLink(allPath.slice(0, length).join('/'), appId);
		if (!pl) { continue; }
		// 创建 link 用 info
		const linkInfo = Object.create(info as MiniInfo<Ext>) as MiniInfo<Ext>;
		try {
			const ext = await extendsInt(appId);
			for (let key of ['app', 'extend', 'fs', 'db', 'config', 'log']) {
				if (key in ext) { linkInfo[key] = ext[key]; }
			}
		} catch(e) {}

		//获取对应路径信息
		for await (const pathInfo of pl(allPath.slice(length), linkInfo, restful)) {
			if (!pathInfo) {
				base = false;
				yield [basePath, module, appId, restful];
				continue;
			}
			const [p, mp] = pathInfo;
			//递归查找
			yield* pathRecursion.call(this,
				allPath.slice(length + p.length),
				mp,
				typeof pathInfo[2] === 'string' ? pathInfo[2] : '',
				[...basePath, ...p],
				info,
				getRestful(restful, typeof pathInfo[2] === 'string' ? pathInfo[3] : pathInfo[2])
			);
		}
	}
	if (base) { yield [basePath, module, appId, restful]; }
}

/** 路径匹配 */
interface pathMatch<Ext extends ExtendedObject = {user: object}> {
	/**
	 * @param path    原始路径数组（特别的，path[0] 表示域名）
	 * @param info    处理用基本信息
	 * @param restful restful 参数，如果不为 restful 模式，请忽略此参数
	 */
	(
		path: string[],
		linkInfo: MiniInfo<Ext>,
		restful?: object,
	): AsyncIterableIterator<PathDescribe>;
}
/**
 * 路径匹配
 * @param path    原始路径数组（特别的，path[0] 表示域名）
 * @param info    处理用基本信息
 * @param restful restful 参数，如果不为 restful 模式，请忽略此参数
 */
async function* pathMatch<Ext extends ExtendedObject = {user: object}>(
	this: void | { getList: Map.list; getLink?: Link.get<Ext>; pecursion?: pathRecursion; config?: Config; },
	path: string[],
	info: MiniInfo<Ext>,
	restful?: object,
): AsyncIterableIterator<PathDescribe> {
	const { getList = Map.list, getLink = Link.get, config = undefined, } = this || {};
	const { pecursion = pathRecursion.bind({getLink, config}) as pathRecursion, } = this || {};
	if(!path.length) { return; }
	path = Array.from(path);
	if (!path[path.length - 1]) { path.pop(); }
	for(let [paths, module, appId] of getList()) {
		//路径长度必须符合要求
		if (path.length < paths.length) { continue; }
		let isMatch = true;
		//逐层路径相等
		// 如果路由域名(paths[0])为空则跳过比较
		for ( let i = path[0] && paths[0] ? 0 : 1, l = paths.length; i < l; i++ ) {
			if (path[i] !== paths[i]) {
				isMatch = false;
				break;
			}
		}
		if (!isMatch) { continue; }
		yield* pecursion(path.slice(paths.length), module, appId || '', paths, info, restful);
	}
}
export{
	pathRecursion,
	pathMatch,
	pathRecursion as recursion,
	pathMatch as match,
}
export default pathMatch;
export function init<Ext extends ExtendedObject = {user: object}>(
	config: Config,
	getList: Map.list,
	getLink: Link.get<Ext>,
): {
	match: pathMatch,
	recursion: pathRecursion<Ext>,
} {
	const recursion = pathRecursion.bind({ config, getLink });
	const match = pathMatch.bind({ config, getList, getLink, recursion });
	return { match, recursion, }
}